// NOTE: Deliberately importing from @apollo/client
import { useQuery } from '@apollo/client'
import {
  Braces,
  ChartLine,
  Globe,
  Logs,
  Rocket,
  Scan,
  Settings,
  SquareTerminal,
  Waypoints,
  Lock,
  Server,
  HardDrive,
} from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/sv/sidebar'
import { useCompanyPath, useIdCompany, useRBAC } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { APP_DISPLAY_NAME_QUERY } from '@/routes/app/queries'
import type {
  AppDisplayNameQuery,
  AppDisplayNameQueryVariables,
} from '@/routes/app/queries.types.gen'

const items = [
  {
    title: 'Overview',
    url: '/app/:appName/overview',
    icon: Scan,
    activeTags: ['overview'],
  },
  {
    title: 'Deployments',
    url: '/app/:appName/deployments',
    icon: Rocket,
    activeTags: ['/deployments', '/deployment/'],
  },
  {
    title: 'Logs',
    url: '/app/:appName/logs',
    icon: Logs,
    activeTags: ['logs'],
  },
  {
    title: 'Analytics',
    url: '/app/:appName/analytics',
    icon: ChartLine,
    activeTags: ['analytics'],
  },
  {
    title: 'Environment variables',
    url: '/app/:appName/environment-variables',
    icon: Braces,
    activeTags: ['environment-variables'],
  },
  {
    title: 'Processes',
    url: '/app/:appName/processes',
    icon: Server,
    activeTags: ['processes', 'process/'],
  },
  {
    title: 'Disks',
    url: '/app/:appName/disks',
    icon: HardDrive,
    activeTags: ['disks'],
  },
  {
    title: 'Domains',
    url: '/app/:appName/domains',
    icon: Globe,
    activeTags: ['domains'],
  },
  {
    title: 'Networking',
    url: '/app/:appName/networking',
    icon: Waypoints,
    activeTags: ['networking'],
  },
  {
    title: 'Web terminal',
    url: '/app/:appName/web-terminal',
    icon: SquareTerminal,
    activeTags: ['web-terminal'],
  },
  {
    title: 'Settings',
    url: '/app/:appName/settings',
    icon: Settings,
    activeTags: ['/settings'],
  },
] as const

export const AppNav = () => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const location = useLocation()
  const { getCompanyPath } = useCompanyPath()
  const { appName = '' } = useParams<{ appName: string }>()
  const { data } = useQuery<AppDisplayNameQuery, AppDisplayNameQueryVariables>(
    APP_DISPLAY_NAME_QUERY,
    { variables: { appName }, fetchPolicy: 'cache-only' }
  )

  const idApp = useMemo(() => data?.app?.id ?? '', [data])
  const idProjectApp = useMemo(() => data?.app?.idProject ?? '', [data])
  const hasAccess = useMemo(
    () =>
      data === undefined
        ? true
        : rbac.company(idCompany).project(idProjectApp).app(idApp).view.can(),
    [rbac, idCompany, idApp, idProjectApp, data]
  )

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const isActive = item.activeTags.some((tag) =>
              location.pathname.includes(tag)
            )
            return (
              <SidebarMenuItem
                key={item.url}
                title={item.title}
                className={cn(
                  !hasAccess
                    ? 'opacity-50 cursor-not-allowed pointer-events-none'
                    : undefined
                )}
              >
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  disabled={!hasAccess}
                >
                  {hasAccess ? (
                    <Link
                      to={getCompanyPath({
                        pathname: item.url,
                        variables: { appName },
                      })}
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Lock />
                      <span>{item.title}</span>
                    </div>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
